import { state } from "./core.js"
import { stabilizeImageMap } from "./stabilizer.js"

const MEDIABUNNY_URLS = [
  "https://cdn.jsdelivr.net/npm/mediabunny@1.44.2/+esm",
  "https://esm.sh/mediabunny@1.44.2?bundle",
]

let mediaModulePromise = null

async function loadFromUrls() {
  const errors = []
  for (const url of MEDIABUNNY_URLS) {
    try {
      return await import(url)
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error))
    }
  }
  throw new Error(`Could not load the media decoder. ${errors.join(" | ")}`)
}

export async function loadMediaModule() {
  if (globalThis.__MEDIABUNNY_TEST_MODULE__) return globalThis.__MEDIABUNNY_TEST_MODULE__
  if (!mediaModulePromise) {
    mediaModulePromise = loadFromUrls().catch((error) => {
      mediaModulePromise = null
      throw error
    })
  }
  return mediaModulePromise
}

function normalizePackets(packets, fallbackFps) {
  packets.sort((a, b) => a.timestamp - b.timestamp || a.sequenceNumber - b.sequenceNumber)
  if (!packets.length) throw new Error("The video track contains no frames.")

  const firstTimestamp = packets[0].timestamp
  const timestamps = packets.map((packet) => Math.max(0, packet.timestamp - firstTimestamp))
  const durations = packets.map((packet, index) => {
    if (Number.isFinite(packet.duration) && packet.duration > 0) return packet.duration
    const next = packets[index + 1]
    if (next && next.timestamp > packet.timestamp) return next.timestamp - packet.timestamp
    return 1 / Math.max(1, fallbackFps)
  })

  return { firstTimestamp, timestamps, durations }
}

export async function openMediaFile(file, onProgress = () => {}) {
  const module = await loadMediaModule()
  const { Input, ALL_FORMATS, BlobSource, EncodedPacketSink } = module
  if (!Input || !ALL_FORMATS || !BlobSource || !EncodedPacketSink) {
    throw new Error("The loaded media module is missing required APIs.")
  }

  const input = new Input({
    source: new BlobSource(file),
    formats: ALL_FORMATS,
  })

  try {
    onProgress("Reading video track metadata…")
    const videoTrack = await input.getPrimaryVideoTrack()
    if (!videoTrack) throw new Error("This file has no video track.")

    if (typeof videoTrack.canDecode === "function") {
      const decodable = await videoTrack.canDecode()
      if (!decodable) throw new Error("This browser cannot decode the video's codec with WebCodecs.")
    }

    const [displayWidth, displayHeight, trackEndTimestamp] = await Promise.all([
      videoTrack.getDisplayWidth(),
      videoTrack.getDisplayHeight(),
      videoTrack.computeDuration(),
    ])

    if (!displayWidth || !displayHeight) throw new Error("The video has invalid dimensions.")

    onProgress("Reading exact frame timestamps…")
    const packetSink = new EncodedPacketSink(videoTrack)
    const packets = []
    let packetCounter = 0
    for await (const packet of packetSink.packets(undefined, undefined, { metadataOnly: true })) {
      packets.push({
        timestamp: Number(packet.timestamp) || 0,
        duration: Number(packet.duration) || 0,
        sequenceNumber: Number(packet.sequenceNumber) || packetCounter,
      })
      packetCounter += 1
      if (packetCounter % 500 === 0) onProgress(`Reading exact frame timestamps… ${packetCounter}`)
    }

    const fallbackFps = 30
    const normalized = normalizePackets(packets, fallbackFps)
    const packetEnd = normalized.timestamps.at(-1) + normalized.durations.at(-1)
    const trackDuration = Math.max(0, Number(trackEndTimestamp) - normalized.firstTimestamp)
    const sourceDuration = Math.max(packetEnd, trackDuration)
    const sourceFps = normalized.timestamps.length / Math.max(0.001, sourceDuration)

    return {
      module,
      input,
      videoTrack,
      firstTimestamp: normalized.firstTimestamp,
      width: Number(displayWidth),
      height: Number(displayHeight),
      duration: sourceDuration,
      fps: sourceFps,
      timestamps: normalized.timestamps,
      durations: normalized.durations,
      frameCount: normalized.timestamps.length,
    }
  } catch (error) {
    input.dispose?.()
    throw error
  }
}

function cloneCanvas(canvas) {
  const copy = document.createElement("canvas")
  copy.width = canvas.width
  copy.height = canvas.height
  copy.getContext("2d", { alpha: false }).drawImage(canvas, 0, 0)
  return copy
}

async function detachCanvas(canvas) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(canvas)
    } catch {
      // Canvas copy fallback below.
    }
  }
  return cloneCanvas(canvas)
}

export async function decodeFrameImages(sourceIndices, dimensions, options = {}) {
  if (!state.videoTrack || !state.mediaModule) throw new Error("No decoded video track is available.")
  const { CanvasSink } = state.mediaModule
  if (!CanvasSink) throw new Error("The media module does not provide CanvasSink.")

  const uniqueIndices = [...new Set(sourceIndices)]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < state.sourceFrameTimestamps.length)
    .sort((a, b) => a - b)
  if (!uniqueIndices.length) return new Map()

  const sink = new CanvasSink(state.videoTrack, {
    width: dimensions.width,
    height: dimensions.height,
    fit: "fill",
    poolSize: 1,
    decoderOptions: { hardwareAcceleration: "prefer-hardware" },
  })
  const actualTimestamps = uniqueIndices.map((index) => state.sourceFirstTimestamp + state.sourceFrameTimestamps[index])
  const result = new Map()
  let completed = 0

  for await (const wrapped of sink.canvasesAtTimestamps(actualTimestamps)) {
    if (options.isCancelled?.()) throw new DOMException("Operation cancelled.", "AbortError")
    const sourceIndex = uniqueIndices[completed]
    if (!wrapped?.canvas) throw new Error(`Could not decode source frame ${sourceIndex + 1}.`)
    const image = await detachCanvas(wrapped.canvas)
    result.set(sourceIndex, image)
    completed += 1
    options.onProgress?.(completed, uniqueIndices.length)
  }

  if (completed !== uniqueIndices.length) {
    for (const image of result.values()) image.close?.()
    throw new Error(`Decoded ${completed} of ${uniqueIndices.length} requested frames.`)
  }

  try {
    return await stabilizeImageMap(result, uniqueIndices, dimensions, options)
  } catch (error) {
    closeImageMap(result)
    throw error
  }
}

export function closeImageMap(map) {
  if (!map) return
  for (const image of map.values()) image?.close?.()
  map.clear?.()
}
